<template>
  <div class="login-page">
    <!-- 로딩 컴포넌트 -->
    <LodingComponent :is-visible="loading" :text="isImpersonating ? '대리 로그인 중...' : '로그인 중...'" />
    
    <v-card class="login-card" elevation="8">
            <v-card-title class="text-center pl-6 pr-6 pt-8 pb-0">
              <img :src="logoImage" height="60" width="auto" class="mb-4 logo-filter">
              <div class="text-h4 font-weight-bold">로그인</div>
              <div class="text-body-2 text-medium-emphasis mt-2">재가장기요양센터 관리 시스템</div>
            </v-card-title>
            
            <v-card-text class="pa-6">
              <v-form ref="loginForm" @submit.prevent="handleLogin">
                <v-text-field
                  v-model="loginData.loginId"
                  label="아이디"
                  variant="outlined"
                  :rules="[rules.required]"
                  prepend-inner-icon="mdi-account"
                  class="mb-3"
                  :disabled="loading"
                  clearable
                ></v-text-field>
                
                <v-text-field
                  v-model="loginData.loginPw"
                  label="비밀번호"
                  type="password"
                  variant="outlined"
                  :rules="[rules.required]"
                  prepend-inner-icon="mdi-lock"
                  :disabled="loading"
                  clearable
                  @keyup.enter="handleLogin"
                ></v-text-field>
                
                <v-checkbox
                  v-model="saveId"
                  label="아이디 저장"
                  color="primary"
                  :disabled="loading"
                ></v-checkbox>
              </v-form>
            </v-card-text>
            
            <v-card-actions class="pl-6 pr-6 pt-0">
              <v-btn
                @click="handleLogin"
                color="primary"
                variant="flat"
                block
                size="large"
                :loading="loading"
                :disabled="!isFormValid || loading"
                rounded="lg"
              >
                <span v-if="!loading">로그인</span>
                <span v-else>로그인 중...</span>
              </v-btn>
            </v-card-actions>
            
            <v-divider></v-divider>
            
            <v-card-actions class="pl-6 pr-6 pt-0">
              <v-btn
                variant="text"
                block
                :disabled="loading"
                prepend-icon="mdi-account-plus"
                @click="handleSignup"
              >
                회원가입
              </v-btn>
            </v-card-actions>
    </v-card>
    
    <!-- 스낵바 컴포넌트 -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      location="top"
    >
      {{ snackbar.text }}
      <template v-slot:actions>
        <v-btn
          color="white"
          variant="text"
          @click="snackbar.show = false"
        >
          닫기
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';

interface LoginData {
  loginId: string;
  loginPw: string;
}

const store = useStore();
const router = useRouter();
const route = useRoute();

// 대리 로그인 처리 중 상태
const isImpersonating = ref(false);

// 이미지 경로 설정
const logoImage = ref(new URL('../../../assets/images/hypehive-logo.png', import.meta.url).href);

// 반응형 데이터
const loginData = ref<LoginData>({
  loginId: '',
  loginPw: ''
});

const saveId = ref(false);
const loginForm = ref();
const isSubmitting = ref(false);

// 폼 유효성 검사 규칙
const rules = {
  required: (value: string) => !!value || '필수 입력 항목입니다.'
};

// 폼 유효성 검사
const isFormValid = computed(() => {
  return loginData.value.loginId.trim() !== '' && 
         loginData.value.loginPw.trim() !== '';
});

// store에서 로딩 상태 가져오기
const loading = computed(() => store.getters.loginLoading || isSubmitting.value || isImpersonating.value);

// 스낵바 상태 가져오기
const snackbar = computed(() => store.getters.snackbar);

// 로그인 처리
const handleLogin = async () => {
  if (!isFormValid.value || isSubmitting.value) return;
  
  isSubmitting.value = true;
  
  try {
    const result = await store.dispatch('performLogin', {
      loginId: loginData.value.loginId,
      loginPw: loginData.value.loginPw,
      autoLogin: false // 자동 로그인 기능 제거
    });
    
    if (result.success) {
      // 아이디 저장 처리
      if (saveId.value) {
        localStorage.setItem('savedLoginId', loginData.value.loginId);
      } else {
        localStorage.removeItem('savedLoginId');
      }
      
      // m_role에 따라 분기 처리
      if (store.getters.isAdmin) {
        // Admin인 경우 관리자 회원관리 페이지로 이동
        router.push('/admin/members');
      } else {
        // 일반 사용자인 경우 임금 관리 페이지로 이동
        router.push('/pgm/wage/WageProcess');
      }
    } else {
      // 로그인 실패 시 비밀번호 필드 초기화 및 포커스
      loginData.value.loginPw = '';
      // 다음 틱에서 비밀번호 필드에 포커스
      setTimeout(() => {
        const passwordField = document.querySelector('input[type="password"]') as HTMLInputElement;
        if (passwordField) {
          passwordField.focus();
        }
      }, 100);
    }
  } catch (error: any) {
    console.error('로그인 오류:', error);
    
    // 예외 발생 시에도 비밀번호 필드 초기화
    loginData.value.loginPw = '';
    setTimeout(() => {
      const passwordField = document.querySelector('input[type="password"]') as HTMLInputElement;
      if (passwordField) {
        passwordField.focus();
      }
    }, 100);
  } finally {
    isSubmitting.value = false;
  }
};

// 회원가입 처리
const handleSignup = () => {
  router.push('/pgm/signup');
};

// 대리 로그인 세션 설정
const handleImpersonateLogin = async (token: string) => {
  isImpersonating.value = true;
  
  try {
    // store 액션을 통해 대리 로그인 처리
    const result = await store.dispatch('performImpersonateLogin', token);
    
    if (result.success) {
      // 임금관리 페이지로 이동
      router.push('/pgm/wage/WageProcess');
    } else {
      // 실패 시 로그인 페이지에 머무름 (URL에서 토큰 제거)
      router.replace('/pgm/login');
    }
  } catch (error) {
    console.error('대리 로그인 세션 설정 오류:', error);
    router.replace('/pgm/login');
  } finally {
    isImpersonating.value = false;
  }
};

// 컴포넌트 마운트 시 이미 로그인된 사용자 확인 및 저장된 아이디 불러오기
onMounted(async () => {
  // URL에서 impersonate 토큰 확인 (대리 로그인)
  const impersonateToken = route.query.impersonate as string;
  
  if (impersonateToken) {
    // 대리 로그인 토큰이 있으면 sessionStorage에 저장하고 세션 설정
    // (쿠키는 건드리지 않음 - Admin 세션 유지)
    await handleImpersonateLogin(impersonateToken);
    return;
  }
  
  await store.dispatch('validateAuth');
  if (store.getters.isLoggedIn) {
    // m_role에 따라 분기 처리
    if (store.getters.isAdmin) {
      router.push('/admin/members');
    } else {
      router.push('/pgm/wage/WageProcess');
    }
  } else {
    // 저장된 아이디 불러오기
    const savedLoginId = localStorage.getItem('savedLoginId');
    if (savedLoginId) {
      loginData.value.loginId = savedLoginId;
      saveId.value = true;
    }
  }
});
</script>

<style scoped>
.login-page {

  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 50px);
  min-height: 600px;
  margin: auto;
}

.login-card {
  min-width: 450px;
  min-height: 600px;
  max-width: 450px;
  max-height: 600px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
}

.login-card .v-card-title {
  background-color: #ffffff;
  color: #333333;
  border-bottom: 1px solid #e0e0e0;
  border-radius: 8px 8px 0 0;
  flex-shrink: 0;
}

.logo-filter {
  filter: none;
}

.v-card-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.v-card-actions {
  flex-shrink: 0;
}

/* 폰트 적용 */
.login-page {
  font-family: 'Noto Sans KR', sans-serif !important;
}

.login-page * {
  font-family: 'Noto Sans KR', sans-serif !important;
}
</style>
